import { FC, useState, useEffect, useMemo, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Label } from "./ui/label"
import { Badge } from "./ui/badge"
import { Search } from "lucide-react"
import { Input } from "./ui/input"
import { Button } from "./ui/button"

import { useGetPlatformDevices } from "@/hooks/api/use-get-platform-devices"
import type { PlatformMeterInfoDTO } from "@/gen/api"
import { AssignDevicePlatformEnum } from "@/gen/api"

const platformValue = (platform?: string) => {
  if (platform?.toLowerCase() === "temetra") return AssignDevicePlatformEnum.Temetra
  if (platform?.toLowerCase() === "onconnect") return AssignDevicePlatformEnum.Onconnect
  return AssignDevicePlatformEnum.C8y
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormApi = any

interface DeviceAssignmentProps {
  councilId: string
  form: FormApi
}

const getDeviceKey = (device: { platformId?: string; platform?: string }) => {
  return `${device.platformId || "unknown"}-${device.platform || "unknown"}`
}

const DeviceAssignment: FC<DeviceAssignmentProps> = ({ councilId, form }) => {
  const [selectedDevices, setSelectedDevices] = useState<PlatformMeterInfoDTO[]>([])
  const [deviceSearchQuery, setDeviceSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(deviceSearchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [deviceSearchQuery])

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetPlatformDevices(
    councilId,
    debouncedSearch || undefined,
  )

  const allDevices = useMemo(() => {
    return data?.pages.flatMap((page) => page.devices) ?? []
  }, [data])

  const updateForm = useCallback(
    (devices: PlatformMeterInfoDTO[]) => {
      const formDevices: { platformId: string; platform: string }[] = devices.map((d) => ({
        platformId: d.platformId || "",
        platform: platformValue(d.platform),
      }))
      form.setFieldValue("devices", formDevices)
      form.setFieldValue(
        "deviceIds",
        devices.map((d) => d.platformId || ""),
      )
    },
    [form],
  )

  const handleDeviceToggle = useCallback(
    (device: PlatformMeterInfoDTO, checked: boolean) => {
      setSelectedDevices((prev) => {
        const next = checked
          ? [...prev, device]
          : prev.filter((d) => getDeviceKey(d) !== getDeviceKey(device))
        updateForm(next)
        return next
      })
    },
    [updateForm],
  )

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedDevices((prev) => {
        const existingKeys = new Set(prev.map((d) => getDeviceKey(d)))
        let next: PlatformMeterInfoDTO[]
        if (checked) {
          const newDevices = allDevices.filter((d) => !existingKeys.has(getDeviceKey(d)))
          next = [...prev, ...newDevices]
        } else {
          const keysToRemove = new Set(allDevices.map((d) => getDeviceKey(d)))
          next = prev.filter((d) => !keysToRemove.has(getDeviceKey(d)))
        }
        updateForm(next)
        return next
      })
    },
    [allDevices, updateForm],
  )

  const removeDevice = useCallback(
    (device: PlatformMeterInfoDTO) => {
      setSelectedDevices((prev) => {
        const next = prev.filter((d) => getDeviceKey(d) !== getDeviceKey(device))
        updateForm(next)
        return next
      })
    },
    [updateForm],
  )

  const selectAllState = useMemo(() => {
    if (allDevices.length === 0) {
      return { checked: false, indeterminate: false }
    }

    const selectedCount = allDevices.filter((device) =>
      selectedDevices.some((s) => getDeviceKey(s) === getDeviceKey(device)),
    ).length

    if (selectedCount === 0) {
      return { checked: false, indeterminate: false }
    } else if (selectedCount === allDevices.length) {
      return { checked: true, indeterminate: false }
    } else {
      return { checked: false, indeterminate: true }
    }
  }, [allDevices, selectedDevices])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Assign Devices ({selectedDevices.length} selected)</CardTitle>
        <p className="text-sm text-muted-foreground">Select devices to assign to this customer</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Selected Devices */}
        {selectedDevices.length > 0 && (
          <div className="space-y-2">
            <Label>Selected Devices</Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/50">
              {selectedDevices.map((device) => {
                const deviceKey = getDeviceKey(device)
                return (
                  <Badge
                    key={deviceKey}
                    variant="default"
                    className="flex items-center gap-2 px-3 py-1"
                  >
                    <span className="text-xs">{device.name || device.platformId}</span>
                    <button
                      type="button"
                      onClick={() => removeDevice(device)}
                      className="hover:bg-white/20 rounded-full p-0.5"
                    >
                      ×
                    </button>
                  </Badge>
                )
              })}
            </div>
          </div>
        )}

        {/* Device Search */}
        <div className="space-y-2">
          <Label htmlFor="device-search">Available Devices</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
            <Input
              id="device-search"
              placeholder="Search devices by name or platform ID..."
              value={deviceSearchQuery}
              onChange={(e) => setDeviceSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        {/* Select All Controls */}
        {allDevices.length > 0 && (
          <div className="flex items-center justify-between py-2 px-1">
            <div className="flex items-center gap-x-2">
              <input
                type="checkbox"
                id="select-all-devices"
                className="size-4 rounded-sm border border-primary"
                checked={selectAllState.checked}
                onChange={(event) => handleSelectAll(event.target.checked)}
                ref={(ref) => {
                  if (ref) {
                    ref.indeterminate = selectAllState.indeterminate
                  }
                }}
              />
              <Label htmlFor="select-all-devices" className="text-sm font-normal cursor-pointer">
                Select All
              </Label>
            </div>
            <Badge variant="secondary" className="text-xs">
              {
                allDevices.filter((device) =>
                  selectedDevices.some((s) => getDeviceKey(s) === getDeviceKey(device)),
                ).length
              }{" "}
              of {allDevices.length} selected
            </Badge>
          </div>
        )}

        {/* Device List */}
        <div className="border rounded-lg max-h-96 overflow-y-auto">
          {isLoading && allDevices.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <p>Loading devices...</p>
            </div>
          ) : allDevices.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <p>No devices found</p>
            </div>
          ) : (
            <div className="divide-y">
              {allDevices.map((device) => {
                const deviceKey = getDeviceKey(device)
                const isSelected = selectedDevices.some((s) => getDeviceKey(s) === deviceKey)
                return (
                  <div
                    key={deviceKey}
                    className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                  >
                    <label htmlFor={`device-${deviceKey}`}>
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id={`device-${deviceKey}`}
                          className="mt-0.5 size-4 rounded-sm border border-primary"
                          checked={isSelected}
                          onChange={(event) => handleDeviceToggle(device, event.target.checked)}
                          onClick={(event) => event.stopPropagation()}
                        />
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{device.name || "Unnamed Device"}</p>
                            <Badge variant="outline" className="text-xs">
                              {device.platform || "Unknown"}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Platform ID: {device.platformId || "N/A"}
                          </p>
                        </div>
                      </div>
                    </label>
                  </div>
                )
              })}
              {hasNextPage && (
                <div className="p-4 text-center">
                  <Button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    variant="outline"
                  >
                    {isFetchingNextPage ? "Loading..." : "Load More"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default DeviceAssignment
